/**
 * DOM layering — the single source of truth for every z-index in the admin app.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS FILE EXISTS
 * ---------------------------------------------------------------------------
 * Before this, SEVEN admin surfaces all sat at a bare `z-50`: the dialog
 * backdrop, the dialog itself, dropdown menus, submenus, the select listbox,
 * popovers and tooltips. Every one of those is a Radix portal appended to
 * `document.body`, so at equal z-index their order was decided by **DOM mount
 * order** — i.e. by whichever happened to open last, not by any rule a reader
 * could reason about. Two of them were genuinely wrong:
 *
 *   • the dialog BACKDROP tied with the dialog CONTENT it is supposed to sit
 *     behind (it worked only because Radix happens to render the overlay first);
 *   • a TOOLTIP tied with the dialog and the menus it can be triggered from, so
 *     a hover bubble could render underneath the surface that owns it.
 *
 * The bands below replace that tie with an ordering somebody chose on purpose.
 *
 * ---------------------------------------------------------------------------
 * HOW TO USE THIS
 * ---------------------------------------------------------------------------
 * 1. Pick a band by MEANING, not by picking a bigger number than the thing you
 *    are currently losing to. If nothing fits, add a band here with a sentence
 *    saying what it is for — do not `+1` an existing one.
 * 2. In JSX use the Tailwind utility (`z-panel`, `z-overlay`, `z-tooltip`, …).
 *    These are generated from the `--z-index-*` entries of the `@theme` block
 *    in `src/styles/globals.css`, which MIRRORS the numbers below — keep the
 *    two in sync. The same file re-maps the UI kit's own z-index scale
 *    (`.sui-dialog`, `.sui-surface`, `.sui-tooltip`, …) onto these bands.
 * 3. If you are still losing after picking the right band, it is almost always a
 *    stacking-CONTEXT problem, not a z-index one: an ancestor with a transform,
 *    `backdrop-filter`, `opacity < 1` or `isolation` traps its descendants in a
 *    nested context, where an inner `z-index: 9999` cannot beat that ancestor's
 *    sibling at `z-index: 1`. Portal out or drop the offending property; raising
 *    the number will not help.
 *
 * The vocabulary here is deliberately the SAME as the main client app's
 * (`client/src/lib/layers.ts`) so the two do not teach different languages —
 * but only the bands this app actually uses are declared. That file carries the
 * long-form rationale and two written-up incidents; read it before adding a
 * band here. See also `docs/Z_INDEX_AUDIT.md`.
 *
 * ---------------------------------------------------------------------------
 * THE BANDS (low → high)
 * ---------------------------------------------------------------------------
 * CANVAS_OVERLAY  10  Chrome painted on top of a widget but still INSIDE it —
 *                     the clock-picker's dial labels over its SVG hand. Always
 *                     local to its own container.
 * PANEL           20  In-flow panel furniture: a sticky table header over its
 *                     own scrolling body.
 * CHROME          30  The sticky app header (the kit's `.sui-shell__header`):
 *                     above page content and sticky table headers.
 * BOTTOM_NAV      32  The kit's mobile bottom navigation bar.
 * FORM_ACTIONS    34  The kit's floating save bar (`FloatingFormActions`) —
 *                     it floats over page content and must clear BOTTOM_NAV.
 * SCROLL_TOP      36  The floating scroll-to-top button: over everything in
 *                     the page (incl. the bottom nav and save bar), but — like
 *                     all four page-chrome bands — BELOW SCRIM, so no page
 *                     furniture can float over a modal backdrop.
 * SCRIM           40  A backdrop belonging to an OVERLAY surface. One band
 *                     BELOW its content, so the pair can never tie.
 * OVERLAY         50  Modals and every portalled popper: dropdown, submenu,
 *                     select listbox, popover. shadcn/ui ships all of these at
 *                     50, so 50 is effectively fixed by upstream. These may
 *                     legitimately share a band — they are mutually exclusive
 *                     in practice, and a menu opened FROM a dialog mounts later
 *                     and so lands above it.
 * TOAST          110  Transient notifications (the kit's `.sui-toaster`). Above
 *                     every modal so a toast fired from a dialog is readable,
 *                     below TOOLTIP so a hover bubble is never covered by one.
 * TOOLTIP        120  Every hover bubble. Above ALL the surfaces a tooltip
 *                     trigger can live on, because a tooltip is transient,
 *                     non-interactive chrome: if its trigger is reachable, the
 *                     bubble must be visible. At the shadcn default of OVERLAY
 *                     it tied with the dialog and menus it opens from.
 */

/** Numeric layer values. Prefer the Tailwind utilities in JSX. */
export const Z = {
  CANVAS_OVERLAY: 10,
  PANEL: 20,
  CHROME: 30,
  BOTTOM_NAV: 32,
  FORM_ACTIONS: 34,
  SCROLL_TOP: 36,
  SCRIM: 40,
  OVERLAY: 50,
  TOAST: 110,
  TOOLTIP: 120,
} as const;

export type LayerName = keyof typeof Z;

/**
 * Tailwind utility class for each band — the same tokens the `@theme` block in
 * `src/styles/globals.css` generates. Write the literal (`className="z-panel"`)
 * when the band is known at author time; this map is for the rare runtime choice, and it is what `cn()`
 * registers as a conflict group.
 */
export const Z_CLASS = {
  CANVAS_OVERLAY: 'z-canvas-overlay',
  PANEL: 'z-panel',
  CHROME: 'z-chrome',
  BOTTOM_NAV: 'z-bottom-nav',
  FORM_ACTIONS: 'z-form-actions',
  SCROLL_TOP: 'z-scroll-top',
  SCRIM: 'z-scrim',
  OVERLAY: 'z-overlay',
  TOAST: 'z-toast',
  TOOLTIP: 'z-tooltip',
} as const satisfies Record<LayerName, string>;
